import { useCallback, useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { alpha, darken, lighten } from '@material-ui/core';
import clsx from 'clsx';

import { DATATABLE_ROW_CLASS, INNER_CELL_VALUE_CLASS } from '../DataTableConstants';
import LoadingRow from './LoadingRow';
import BodyCell from './BodyCell';
import { SELECTIONMODE_SINGLE, useSelection, useSelectionActions } from '../SelectionContext';
import { useColumns } from '../ColumnContext';
import { useContentWidth } from './RenderContext';

const MUI_TABLE_CELL_CLS = 'MuiTableCell-root';
const useStyles = makeStyles((theme) => ({
    tableRow: {
        display: 'block',

        // this may seem odd, but it fixes Safari row border alignment
        verticalAlign: 'top',

        '@media print': {
            pageBreakInside: 'avoid',
        },

        [`& .${MUI_TABLE_CELL_CLS}`]: {
            backgroundColor: theme.palette.background.paper,
        },

        [`&.row-deleted .${INNER_CELL_VALUE_CLASS}`]: {
            textDecoration: 'line-through',
        },

        [`&:hover .${MUI_TABLE_CELL_CLS}`]: {
            backgroundColor:
                theme.palette.type === 'light'
                    ? darken(alpha(theme.palette.background.paper, 1), 0.03)
                    : lighten(alpha(theme.palette.background.paper, 1), 0.06),
        },

        // Use correct background color when hovering rows in path visualization list
        [`&:hover .hop-node-bottom-content .MuiTypography-root`]: {
            backgroundColor:
                theme.palette.type === 'light'
                    ? darken(theme.palette.background.paper, 0.03)
                    : lighten(theme.palette.background.paper, 0.06),
        },

        // For rows that are selectable
        '&.row-selectable': {
            // Make the inner cells have a pointer cursor
            [`& .${INNER_CELL_VALUE_CLASS}`]: {
                cursor: 'pointer',
            },

            // for rows that have been selected
            '&.row-selected': {
                [`& .${MUI_TABLE_CELL_CLS}`]: {
                    backgroundColor:
                        theme.palette.type === 'light'
                            ? lighten(alpha(theme.palette.primary.light, 1), 0.88)
                            : darken(alpha(theme.palette.primary.light, 1), 0.68),
                },

                [`&:hover .${MUI_TABLE_CELL_CLS}`]: {
                    backgroundColor:
                        theme.palette.type === 'light'
                            ? lighten(alpha(theme.palette.primary.light, 1), 0.92)
                            : darken(alpha(theme.palette.primary.light, 1), 0.75),
                },
            },
        },
    },
}));

export default function DataRow(props) {
    const { row, deleted, index } = props;
    const columns = useColumns();
    const contentWidth = useContentWidth();
    const classes = useStyles();
    const rowContent = row ? columns.map(renderColumnCell) : <LoadingRow />;

    const {
        enabled: selectable,
        isSelected,
        needSingleAutoSelectFirst,
        mode,
        totalRows,
        hasSelection,
    } = useSelection();

    const { select, deselect } = useSelectionActions();
    const selected = isSelected(row);

    const clickHandler = useCallback(() => {
        // row may be null, you can't select that
        if (row) {
            (selected ? deselect : select)(row);
        }
    }, [select, deselect, selected, row]);

    // Like VMware Infrastructure=>Create vCenter, when contain Collectors, and which is single mode, would auto select first row
    useEffect(() => {
        if (
            needSingleAutoSelectFirst &&
            SELECTIONMODE_SINGLE === mode &&
            totalRows > 0 &&
            !hasSelection &&
            !deleted &&
            index === 0
        ) {
            clickHandler();
        }
    }, [needSingleAutoSelectFirst, mode, totalRows, hasSelection, deleted, clickHandler, index]);

    return (
        // eslint-disable-next-line
        <div
            className={clsx(classes.tableRow, DATATABLE_ROW_CLASS, {
                'row-selected': selected,
                'row-selectable': selectable,
                'row-deleted': deleted,
            })}
            style={{ width: `${contentWidth}px` }}
            onClick={clickHandler}
        >
            {rowContent}
        </div>
    );

    function renderColumnCell(column) {
        return (
            <BodyCell key={column.id} column={column} tooltip={column.getTooltip(row, column)}>
                {column.renderer(row, column)}
            </BodyCell>
        );
    }
}
